import React from 'react';
import { FormattedNumber } from 'react-intl';
import CurrencyLabel from 'Utils/CurrencyLabel';

const RentalBillingV2 = (props) => {
  const { rental, hideSubtotalPricing, location } = props;
  const findBillingLine = (kind) => {
    if (!rental.billingLines) {
      return null;
    }
    return rental.billingLines.find((line) => line.kind === kind);
  };

  let standardFees = [];
  rental.billingLines?.forEach((bl) => {
    if (bl.kind === 'standardFeesTotal' && bl.aggregatedLines) {
      const globalFeeLines = bl.aggregatedLines.filter(
        (line) => line.kind === 'globalFeeLine'
      );
      standardFees = globalFeeLines.map((line) => ({
        name: line.name,
        finalValue: line.finalValue,
      }));
    }
  });

  const itemFees = rental.billingLines?.filter(
    (line) => line.kind === 'itemFeeLine'
  ).map((line) => ({
    id: line.id || line.name,
    name: line.name,
    finalValue: line.finalValue,
  })) || [];

  const creditCardFees = rental.billingLines?.filter(
    (line) => line.kind === 'creditCardFee'
  ).map((line) => ({
    name: line.name,
    finalValue: line.finalValue,
    displayPercent: line.displayPercent,
  })) || [];

  const discounts = rental.billingLines?.filter(
    (line) => line.kind === 'globalDiscountLine'
  ).map((line) => ({
    name: line.name,
    finalValue: line.finalValue,
    displayPercent: line.displayPercent,
  })) || [];

  const rentalItemsTotal = findBillingLine('rentalItemTotal')?.finalValue || 0;
  const rentalAddOnsTotal = findBillingLine('purchaseTotal')?.finalValue || 0;
  const rentalBundlesTotal = findBillingLine('bundleTotal')?.finalValue || 0;
  const staffingTotal = findBillingLine('staffTotal')?.finalValue || 0;
  const deliveryTotal = findBillingLine('deliveryTotal')?.finalValue || 0;
  const damageWaiverTotal = findBillingLine('damageWaiverFeeTotal')?.finalValue || 0;
  const subtotal = findBillingLine('subtotal')?.finalValue || 0;
  const salesTaxesTotal = findBillingLine('salesTaxesTotal')?.finalValue || 0;
  const additionalTaxesTotal = findBillingLine('additionalTaxesTotal')?.finalValue || 0;
  const tapgoodsFeeTotal = findBillingLine('tapgoodsFeeTotal')?.finalValue || 0;
  const tapgoodsFeePercent = findBillingLine('tapgoodsFeeTotal')?.displayPercent || 0;
  const orderTotal = findBillingLine('orderTotal')?.finalValue || 0;

  const salesTaxes = rental.billingLines?.filter(
    (line) => line.kind === 'salesTaxesLine'
  );

  const hasPriceAvailableOnRequestOnItem = rental.rentalItems.some(
    (ri) => +ri.selectedPrice === 0
  );
  const hasPriceAvailableOnRequestOnAddOn = rental.rentalAddOns.some(
    (ri) => +ri.selectedPrice === 0
  );
  const hasPriceAvailableOnRequestOnBundle = rental.rentalBundles.some(
    (ri) => +ri.selectedPrice === 0
  );
  const hasPriceAvailableOnRequest =
    (hasPriceAvailableOnRequestOnItem ||
      hasPriceAvailableOnRequestOnAddOn ||
      hasPriceAvailableOnRequestOnBundle) &&
    rental.approvalStatus === 'pending';

  return (
    <section className='billing'>
      <h4>Billing</h4>
      <div className='billingDetails'>
        {rental.rentalItems.length > 0 && (
          <div>
            <label>Item(s) Total</label>
            {hideSubtotalPricing ? null : hasPriceAvailableOnRequestOnItem &&
              hasPriceAvailableOnRequest ? (
              <span>Price On Request</span>
            ) : (
              <CurrencyLabel value={rentalItemsTotal} />
            )}
          </div>
        )}
        {rental.rentalAddOns.length > 0 && (
          <div>
            <label>Purchase(s) Total</label>
            {hideSubtotalPricing ? null : hasPriceAvailableOnRequestOnAddOn &&
              hasPriceAvailableOnRequest ? (
              <span>Price On Request</span>
            ) : (
              <CurrencyLabel value={rentalAddOnsTotal} />
            )}
          </div>
        )}

        {rental.rentalBundles.length > 0 && rentalBundlesTotal > 0 && (
          <div>
            <label>Bundle(s) Total</label>
            {hideSubtotalPricing ? null : hasPriceAvailableOnRequestOnBundle &&
              hasPriceAvailableOnRequest ? (
              <span>Price On Request</span>
            ) : (
              <CurrencyLabel value={rentalBundlesTotal} />
            )}
          </div>
        )}

        {staffingTotal > 0 && (
          <div>
            <label>Staffing Total</label>
            {!hideSubtotalPricing && (
              <CurrencyLabel value={staffingTotal} />
            )}
          </div>
        )}

        {deliveryTotal > 0 && (
          <div>
            <label>Delivery</label>
            {!hideSubtotalPricing && (
              <CurrencyLabel value={deliveryTotal} />
            )}
          </div>
        )}
        {damageWaiverTotal > 0 && (
          <div>
            <label>Damage Waiver</label>
            {hideSubtotalPricing ? null : hasPriceAvailableOnRequest &&
              hasPriceAvailableOnRequest ? (
              <span>Price On Request</span>
            ) : (
              <CurrencyLabel value={damageWaiverTotal} />
            )}
          </div>
        )}
        {!hideSubtotalPricing &&
          itemFees.map((fee) => (
            <div key={fee.id}>
              <label>{fee.name}</label>
              <CurrencyLabel value={fee.finalValue} />
            </div>
          ))}
        {!hideSubtotalPricing &&
          standardFees.map((fee, i) => {
            return (
              <div key={i}>
                <label>{fee.name}</label>
                <CurrencyLabel value={fee.finalValue} />
              </div>
            );
          })}
        {!hideSubtotalPricing &&
          discounts.map((discount, i) => {
            return (
              <div key={i}>
                <label>
                  {discount.name}
                  {discount.displayPercent && (
                    <span>
                      {' ('}
                      <FormattedNumber
                        value={discount.displayPercent}
                        style='percent'
                        minimumFractionDigits={2}
                        minimumSignificantDigits={3}
                      />
                      {')'}
                    </span>
                  )}
                </label>
                <CurrencyLabel value={discount.finalValue} />
              </div>
            );
          })}

        <div className='subtotal'>
          <label>Subtotal</label>
          {hideSubtotalPricing ? null : hasPriceAvailableOnRequest &&
            hasPriceAvailableOnRequest ? (
            <span>Price On Request</span>
          ) : (
            <CurrencyLabel value={subtotal} />
          )}
        </div>
        {location && location.taxBreakdown && salesTaxesTotal > 0 && (
          <div className='subtotal'>
            <label>Tax</label>
            <CurrencyLabel value={salesTaxesTotal} />
          </div>
        )}
        {location && location.taxBreakdown ? (
          salesTaxes &&
          salesTaxes.map((tax, i) => {
            return (
              <div key={i} className='subTaxes'>
                <label>
                  {tax.shortName} {' ('}
                  <FormattedNumber
                    value={tax.taxPercent}
                    style='percent'
                    maximumFractionDigits={2}
                  />
                  {')'}
                  {rental.rentalVersioningEnabled &&
                    tax.createdFromOverride && <p>{tax.appliesTo}</p>}
                </label>
                <span>
                  {hideSubtotalPricing ? null : hasPriceAvailableOnRequest ? (
                    <span>Price On Request</span>
                  ) : (
                    <span>
                      <CurrencyLabel value={tax.finalValue} />
                    </span>
                  )}
                </span>
              </div>
            );
          })
        ) : (
          <div>
            <label>Tax</label>
            <span>
              {rental.taxExempt && (
                <label className='caption'>(Tax Exempt)</label>
              )}

              {hideSubtotalPricing ? null : hasPriceAvailableOnRequest ? (
                <span>Price On Request</span>
              ) : (
                <CurrencyLabel value={salesTaxesTotal} />
              )}
            </span>
          </div>
        )}
        {!hideSubtotalPricing &&
          creditCardFees.map((fee, i) => {
            return (
              <div key={i}>
                <label>
                  {fee.name}
                  {fee.displayPercent && (
                    <span>
                      {' ('}
                      <FormattedNumber
                        value={fee.displayPercent}
                        style='percent'
                        minimumFractionDigits={2}
                        minimumSignificantDigits={3}
                      />
                      {')'}
                    </span>
                  )}
                </label>
                <CurrencyLabel value={fee.finalValue} />
              </div>
            );
          })}
        <div>
          <label>Additional Taxes</label>
          <span>
            {hideSubtotalPricing ? null : hasPriceAvailableOnRequest ? (
              <span>Price On Request</span>
            ) : (
              <CurrencyLabel value={additionalTaxesTotal} />
            )}
          </span>
        </div>

        {!hideSubtotalPricing && tapgoodsFeeTotal > 0 && (
          <div>
            <label>
              TapGoods Processing Fee{' '}
              {tapgoodsFeePercent > 0 && (
                <span>
                  (
                  <FormattedNumber
                    value={tapgoodsFeePercent}
                    style='percent'
                    minimumFractionDigits={2}
                    minimumSignificantDigits={3}
                  />
                  )
                </span>
              )}
            </label>
            <span>
              <CurrencyLabel value={tapgoodsFeeTotal} />
            </span>
          </div>
        )}
        <div className='total'>
          <label>Rental Total</label>
          {hideSubtotalPricing ? null : hasPriceAvailableOnRequest ? (
            <span>Price On Request</span>
          ) : (
            <CurrencyLabel value={orderTotal} />
          )}
        </div>
      </div>
    </section>
  );
};

export default RentalBillingV2;
